import type { User } from '../auth/types';
import { getCrmViews } from '../components/crm/crmViews';
import { normalizeReceptionSearch, receptionStudentsSchema } from '../pages/reception/receptionListNavigation';

export interface StudentSearchContext { pathname: string; search: string }

export const canSearchReceptionStudents = (user: User): boolean =>
  getCrmViews(user).some(view => view.id === 'reception');

function studentListContext(context?: StudentSearchContext) {
  const pathname = context?.pathname.replace(/^\/app(?=\/)/, '').replace(/\/$/, '');
  const inList = pathname === '/reception/students' || pathname === '/manager/students';
  const source = new URLSearchParams(inList ? context?.search : '');
  const ownedKeys = new Set(Object.values(receptionStudentsSchema.fields).map(field => field.key));
  const params = new URLSearchParams();
  for (const [key, value] of source) if (ownedKeys.has(key)) params.append(key, value);
  params.delete('student');
  params.delete('studentTab');
  return { pathname: inList ? pathname : '/reception/students', params };
}

/** Preserve the list when opening a record; a new full search starts on page one. */
export function receptionStudentSearchPath(query: string, context?: StudentSearchContext, studentId?: number): string {
  const { pathname, params } = studentListContext(context);
  if (studentId !== undefined) params.set('student', String(studentId));
  else {
    params.set('q', normalizeReceptionSearch(query).trim());
    params.delete('page');
  }
  return `${pathname}?${params.toString()}`;
}

/** Translate the existing All Students URL filters to its canonical API. */
export function receptionStudentSearchParams(query: string, context?: StudentSearchContext): URLSearchParams {
  const { params } = studentListContext(context);
  const categories = params.getAll('category').flatMap(value => value.split(',')).filter(Boolean);
  params.delete('category');
  if (categories.length) params.set('categories', categories.join(','));
  for (const [from, to] of [['groupMembership', 'group_membership'], ['createdFrom', 'created_from'], ['createdTo', 'created_to']] as const) {
    const value = params.get(from);
    params.delete(from);
    if (value) params.set(to, value);
  }
  params.delete('plain');
  params.delete('pageSize');
  params.set('q', normalizeReceptionSearch(query).trim());
  params.set('page', '1');
  params.set('page_size', '10');
  params.set('fields', 'basic');
  return params;
}
